import { useEffect, useState } from 'react';
import { formatBRL, formatDateTimeBR } from '../../utils/format';
import { useToast } from '../../context/ToastContext';
import { RotateCcw, X } from 'lucide-react';

interface AdminOrdersProps {
  initialPaymentStatus?: string;
  onNavigate?: (path: string) => void;
}

export function AdminOrders({ initialPaymentStatus = '' }: AdminOrdersProps) {
  const { showToast } = useToast();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState(initialPaymentStatus);
  const [fulfillmentUpdating, setFulfillmentUpdating] = useState(false);

  // Refund modal state
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [refunding, setRefunding] = useState(false);

  const fetchOrders = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (paymentFilter) params.set('payment_status', paymentFilter);

    fetch(`/api/admin/orders?${params.toString()}`)
      .then((res) => res.json())
      .then((data: any) => setOrders(data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchOrders();
  }, [statusFilter, paymentFilter]);

  const handleOpenDetail = async (orderNumber: string) => {
    try {
      const res = await fetch(`/api/orders/${orderNumber}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedOrder(data);
      }
    } catch {
      showToast('error', 'Erro', 'Falha ao carregar detalhes do pedido.');
    }
  };

  const handleUpdateFulfillment = async (newStatus: string) => {
    if (!selectedOrder) return;
    setFulfillmentUpdating(true);
    try {
      const res = await fetch(`/api/admin/orders/${selectedOrder.id}/fulfillment`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fulfillment_status: newStatus }),
      });

      if (res.ok) {
        showToast('success', 'Fulfillment atualizado', `Status alterado para ${newStatus}.`);
        setSelectedOrder((prev: any) => ({ ...prev, fulfillment_status: newStatus }));
        fetchOrders();
      } else {
        throw new Error('Erro ao atualizar status.');
      }
    } catch (err: unknown) {
      showToast('error', 'Erro', err instanceof Error ? err.message : 'Falha ao atualizar.');
    } finally {
      setFulfillmentUpdating(false);
    }
  };

  const handleIssueRefund = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrder) return;
    setRefunding(true);

    try {
      const res = await fetch(`/api/admin/orders/${selectedOrder.id}/refund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount_cents: selectedOrder.total_cents,
          reason: refundReason || 'Cancelamento solicitado pelo cliente',
        }),
      });

      const data = (await res.json()) as any;
      if (!res.ok) throw new Error(data.error || 'Falha ao processar estorno.');

      showToast('success', 'Reembolso efetuado', 'O pedido foi estornado e os registros financeiros foram atualizados.');
      setShowRefundModal(false);
      setSelectedOrder((prev: any) => ({ ...prev, payment_status: 'refunded', status: 'refunded' }));
      fetchOrders();
    } catch (err: unknown) {
      showToast('error', 'Erro no reembolso', err instanceof Error ? err.message : 'Falha.');
    } finally {
      setRefunding(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border-subtle pb-6">
        <div>
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-primary">
            Gestão de Pedidos & Faturamento
          </span>
          <h1 className="text-2xl sm:text-3xl font-serif font-normal text-ink mt-0.5">
            Pedidos dos Clientes ({orders.length})
          </h1>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={paymentFilter}
            onChange={(e) => setPaymentFilter(e.target.value)}
            className="bg-surface border border-border-subtle px-3 py-2 text-xs rounded-xs focus:outline-none focus:border-primary text-ink"
          >
            <option value="">Todos os Pagamentos</option>
            <option value="paid">Pagos (Aprovados)</option>
            <option value="pending">Pendentes</option>
            <option value="refunded">Reembolsados</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-surface border border-border-subtle px-3 py-2 text-xs rounded-xs focus:outline-none focus:border-primary text-ink"
          >
            <option value="">Todos os Status de Pedido</option>
            <option value="pending">Pendente</option>
            <option value="processing">Em Processamento</option>
            <option value="completed">Concluído</option>
            <option value="cancelled">Cancelado</option>
          </select>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-surface border border-border-subtle rounded-xs overflow-x-auto shadow-subtle">
        <table className="w-full text-left border-collapse text-xs">
          <thead>
            <tr className="border-b border-border-subtle bg-background-alt text-ink-muted font-medium">
              <th className="p-4">Pedido</th>
              <th className="p-4">Data / Hora</th>
              <th className="p-4">Itens</th>
              <th className="p-4">Total</th>
              <th className="p-4">Pagamento</th>
              <th className="p-4">Fulfillment</th>
              <th className="p-4 text-right">Ação</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {loading ? (
              <tr>
                <td colSpan={7} className="p-8 text-center text-ink-muted">
                  Carregando pedidos...
                </td>
              </tr>
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-8 text-center text-ink-muted italic">
                  Nenhum pedido encontrado com os filtros atuais.
                </td>
              </tr>
            ) : (
              orders.map((ord) => (
                <tr key={ord.id} className="hover:bg-background-alt/50 transition-colors">
                  <td className="p-4 font-mono font-medium text-ink">
                    {ord.order_number}
                  </td>
                  <td className="p-4 text-ink-muted">
                    {formatDateTimeBR(ord.created_at)}
                  </td>
                  <td className="p-4 text-ink-muted">
                    {ord.item_count || 1} {ord.item_count === 1 ? 'item' : 'itens'}
                  </td>
                  <td className="p-4 font-mono font-semibold text-ink">
                    {formatBRL(ord.total_cents)}
                  </td>
                  <td className="p-4">
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize ${
                        ord.payment_status === 'paid'
                          ? 'bg-olive-light text-olive'
                          : ord.payment_status === 'refunded'
                          ? 'bg-red-50 text-red-700'
                          : 'bg-amber-50 text-amber-800'
                      }`}
                    >
                      {ord.payment_status}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-background-alt text-ink capitalize border border-border-subtle">
                      {ord.fulfillment_status}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <button
                      onClick={() => handleOpenDetail(ord.order_number)}
                      className="text-primary font-medium hover:underline text-xs"
                    >
                      Gerenciar
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Drawer: Order Detail */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 overflow-hidden" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-ink/40 backdrop-blur-xs"
            onClick={() => setSelectedOrder(null)}
          />

          <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
            <div className="w-screen max-w-lg bg-surface text-ink shadow-2xl flex flex-col border-l border-border-subtle animate-fadeIn">
              {/* Drawer Header */}
              <div className="p-6 border-b border-border-subtle flex items-center justify-between">
                <div>
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-primary">
                    Detalhes do Pedido
                  </span>
                  <h3 className="font-mono text-xl font-bold text-ink">
                    {selectedOrder.order_number}
                  </h3>
                </div>
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="p-1.5 text-ink-muted hover:text-ink rounded-sm"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Drawer Body */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6 text-xs">
                {/* Financial & Fulfillment State */}
                <div className="grid grid-cols-2 gap-3 bg-background-alt p-4 rounded-xs border border-border-subtle">
                  <div>
                    <span className="text-[11px] text-ink-muted uppercase tracking-wider block">Status Financeiro</span>
                    <span className="font-semibold text-sm capitalize mt-0.5 block">
                      {selectedOrder.payment_status}
                    </span>
                  </div>
                  <div>
                    <span className="text-[11px] text-ink-muted uppercase tracking-wider block">Fulfillment</span>
                    <span className="font-semibold text-sm capitalize mt-0.5 block">
                      {selectedOrder.fulfillment_status}
                    </span>
                  </div>
                </div>

                {/* Fulfillment Status Changer */}
                <div className="space-y-2 border-t border-border-subtle pt-4">
                  <label className="font-semibold text-ink uppercase tracking-wider text-[11px] block">
                    Atualizar Estado de Envio / Fulfillment
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {['in_preparation', 'shipped', 'delivered'].map((st) => (
                      <button
                        key={st}
                        disabled={fulfillmentUpdating}
                        onClick={() => handleUpdateFulfillment(st)}
                        className={`p-2 border rounded-xs capitalize text-xs transition-colors ${
                          selectedOrder.fulfillment_status === st
                            ? 'bg-ink text-ink-inverted font-semibold border-ink'
                            : 'border-border-subtle hover:border-ink/30 bg-surface'
                        }`}
                      >
                        {st.replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Decrypted Shipping Address */}
                <div className="space-y-2 border-t border-border-subtle pt-4">
                  <span className="font-semibold text-ink uppercase tracking-wider text-[11px] block">
                    Endereço de Entrega (Decriptado do D1)
                  </span>
                  {selectedOrder.shipping_address ? (
                    <div className="bg-background-alt p-3.5 rounded-xs border border-border-subtle space-y-1 text-ink-muted">
                      <p className="font-medium text-ink">{selectedOrder.shipping_address.recipient_name}</p>
                      <p>{selectedOrder.shipping_address.street}, {selectedOrder.shipping_address.number} {selectedOrder.shipping_address.complement || ''}</p>
                      <p>{selectedOrder.shipping_address.neighborhood} - {selectedOrder.shipping_address.city}/{selectedOrder.shipping_address.state}</p>
                      <p className="font-mono">CEP: {selectedOrder.shipping_address.postal_code}</p>
                    </div>
                  ) : (
                    <p className="text-ink-muted italic">Pedido sem necessidade de remessa física.</p>
                  )}
                </div>

                {/* Items Snapshot */}
                <div className="space-y-3 border-t border-border-subtle pt-4">
                  <span className="font-semibold text-ink uppercase tracking-wider text-[11px] block">
                    Itens Adquiridos
                  </span>
                  <div className="divide-y divide-border-subtle">
                    {selectedOrder.items?.map((item: any) => (
                      <div key={item.id} className="py-2.5 flex justify-between items-start">
                        <div>
                          <p className="font-medium text-ink">{item.product_title_snapshot}</p>
                          <p className="text-ink-muted">{item.variant_title_snapshot} &bull; <span className="font-mono">{item.sku_snapshot}</span></p>
                          <p className="text-ink-muted">Qtd: {item.quantity}</p>
                        </div>
                        <span className="font-mono font-medium text-ink">
                          {formatBRL(item.total_cents)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Totals */}
                <div className="border-t border-border-subtle pt-4 space-y-1.5">
                  <div className="flex justify-between text-ink-muted">
                    <span>Subtotal</span>
                    <span className="font-mono text-ink">{formatBRL(selectedOrder.subtotal_cents)}</span>
                  </div>
                  <div className="flex justify-between text-ink-muted">
                    <span>Frete</span>
                    <span className="font-mono text-ink">{formatBRL(selectedOrder.shipping_cents)}</span>
                  </div>
                  <div className="flex justify-between text-ink font-semibold text-sm pt-2 border-t border-border-subtle">
                    <span>Total Arrecadado</span>
                    <span className="font-mono text-base">{formatBRL(selectedOrder.total_cents)}</span>
                  </div>
                </div>

                {/* Refund Action (Financial) */}
                {selectedOrder.payment_status === 'paid' && (
                  <div className="pt-4 border-t border-border-subtle">
                    <button
                      type="button"
                      onClick={() => setShowRefundModal(true)}
                      className="w-full border border-red-300 text-red-700 hover:bg-red-50 py-2.5 rounded-xs text-xs font-medium transition-colors flex items-center justify-center gap-1.5"
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> Estornar / Reembolsar Pedido
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Refund Confirmation */}
      {showRefundModal && selectedOrder && (
        <div className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-surface w-full max-w-md rounded-xs shadow-elevated border border-border-subtle p-6 space-y-4 animate-fadeIn">
            <h3 className="font-serif text-lg font-medium text-ink">Confirmar Estorno Financeiro</h3>
            <p className="text-xs text-ink-muted leading-relaxed">
              O estorno deduzirá o valor de <strong className="text-ink">{formatBRL(selectedOrder.total_cents)}</strong> das métricas de vendas líquidas e marcará o pedido como estornado.
            </p>

            <form onSubmit={handleIssueRefund} className="space-y-4 text-xs">
              <div>
                <label className="font-semibold text-ink block mb-1">Motivo do Reembolso</label>
                <input
                  type="text"
                  required
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="Ex: Desistência do comprador no prazo do CDC"
                  className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowRefundModal(false)}
                  className="px-4 py-2 border border-border-subtle rounded-xs hover:border-ink/40"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={refunding}
                  className="bg-red-700 text-white px-5 py-2 rounded-xs font-medium hover:bg-red-800 disabled:opacity-40"
                >
                  {refunding ? 'Processando...' : 'Confirmar Estorno'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
